import { type CatalogGroupDto, type WarehouseDto, type WarehouseSettings, type CaseDto, type CompanyDto, type TaskType, taskLabel, type TradeDto, type UserLiteDto } from '@bqcrm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { notifyError } from './notify';

/** Słowniki używane w wielu ekranach (cache 5 min). */
export const useCompanies = () => useQuery({ queryKey: ['companies'], queryFn: () => api<CompanyDto[]>('/companies'), staleTime: 300_000 });
export const useTrades = () => useQuery({ queryKey: ['trades'], queryFn: () => api<TradeDto[]>('/dict/trades'), staleTime: 300_000 });
/** Etykieta zadania — nazwa pracy z ustawień branży (np. „Pomiary elektryczne”), a bez niej rodzaj zadania. */
export const useTaskTypeLabel = () => (t: { taskType: TaskType; workName?: string | null }) => taskLabel(t);
export const useOfficeUsers = () =>
  useQuery({
    queryKey: ['users-lite'],
    queryFn: async () => (await api<UserLiteDto[]>('/dict/users')).filter((u) => !u.isField),
    staleTime: 300_000,
  });
export const useCase = (id: string) => useQuery({ queryKey: ['case', id], queryFn: () => api<CaseDto>(`/cases/${id}`), enabled: !!id });

/**
 * Mutacja operacji na sprawie: serwer zwraca aktualną sprawę, którą wstawiamy do pamięci podręcznej
 * (bez dodatkowego pobierania), i odświeżamy listy oraz historię.
 */
export function useCaseMutation<V>(caseId: string, fn: (v: V) => Promise<CaseDto>, onDone?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (c) => {
      qc.setQueryData(['case', caseId], c);
      void qc.invalidateQueries({ queryKey: ['cases'] });
      void qc.invalidateQueries({ queryKey: ['case-history', caseId] });
      onDone?.();
    },
    onError: notifyError,
  });
}
/** Kogo można wskazać jako opiekuna sprawy: osoby z funkcją „opiekun”, które mogą nadać sprawie bieg. */
export const useOwnerCandidates = () =>
  useQuery({ queryKey: ['owner-candidates'], queryFn: () => api<{ id: string; fullName: string }[]>('/cases/owner-candidates'), staleTime: 300_000 });

/** Magazyn (docs/14): grupy produktów, magazyny dostępne dla pracownika, ustawienia cen. */
export const useCatalogGroups = (enabled = true) => useQuery({ queryKey: ['catalog-groups'], queryFn: () => api<CatalogGroupDto[]>('/catalog/groups'), staleTime: 300_000, enabled });
export const useWarehouses = (enabled = true) => useQuery({ queryKey: ['warehouses'], queryFn: () => api<WarehouseDto[]>('/warehouses'), staleTime: 300_000, enabled });
export const useWarehouseSettings = (enabled = true) => useQuery({ queryKey: ['warehouse-settings'], queryFn: () => api<WarehouseSettings>('/warehouse-settings'), staleTime: 300_000, enabled });
